import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook to check for app version updates
 * @param {Object} options - Configuration options
 * @param {number} options.checkInterval - Interval in milliseconds to check for updates (default: 5 minutes)
 * @param {Function} options.onNewVersion - Callback function when a new version is detected
 * @returns {Object} - { currentVersion, newVersionAvailable }
 */
const useVersionCheck = ({ checkInterval = 5 * 60 * 1000, onNewVersion } = {}) => {
  const [currentVersion, setCurrentVersion] = useState(null);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const intervalRef = useRef(null);
  const initialVersionRef = useRef(null);

  // Load initial version on mount
  useEffect(() => {
    const loadInitialVersion = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now());
        if (response.ok) {
          const data = await response.json();
          setCurrentVersion(data.version);
          initialVersionRef.current = data.version;
        }
      } catch (error) {
        console.warn('Failed to load initial version:', error);
      }
    };

    loadInitialVersion();
  }, []);

  // Set up periodic version checking
  useEffect(() => {
    if (!initialVersionRef.current) {
      return; // Wait for initial version to load
    }

    const checkVersion = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now());
        if (response.ok) {
          const data = await response.json();
          const serverVersion = data.version;

          // Compare versions
          if (serverVersion && serverVersion !== initialVersionRef.current) {
            setNewVersionAvailable(true);
            setCurrentVersion(serverVersion);
            
            // Call callback if provided
            if (onNewVersion) {
              onNewVersion(serverVersion, initialVersionRef.current);
            }
          }
        }
      } catch (error) {
        // Silently fail - don't break the app if version check fails
        console.warn('Version check failed:', error);
      }
    };

    // Check immediately after initial load
    const initialCheckTimeout = setTimeout(checkVersion, 1000);

    // Set up interval for periodic checks
    intervalRef.current = setInterval(checkVersion, checkInterval);

    return () => {
      clearTimeout(initialCheckTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkInterval, onNewVersion]);

  return {
    currentVersion,
    newVersionAvailable,
    resetVersionCheck: () => {
      setNewVersionAvailable(false);
      if (initialVersionRef.current) {
        setCurrentVersion(initialVersionRef.current);
      }
    }
  };
};

export default useVersionCheck;

